import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to always send cookies
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const loginWithGoogle = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/google/url`);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        console.error("No Google OAuth URL returned");
      }
    } catch (err) {
      console.error("Failed to get Google OAuth URL:", err);
    }
  }, []);

  const loginWithEmail = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.success) {
      // Always re-fetch via /auth/me so we get authoritative `has_company`,
      // `default_company_id`, and plan info computed against fresh DB state.
      try {
        const fresh = await axios.get(`${API}/auth/me`);
        setUser(fresh.data);
      } catch {
        setUser(res.data.user);
      }
      return res.data;
    }
    throw new Error(res.data.message || "Login failed");
  }, []);

  const register = useCallback(async (email, password, name, role = "client") => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name, role });
    if (res.data.success) {
      try {
        const fresh = await axios.get(`${API}/auth/me`);
        setUser(fresh.data);
      } catch {
        setUser(res.data.user);
      }
      return res.data;
    }
    throw new Error(res.data.message || "Registration failed");
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // ignore
    }
    setUser(null);
  }, []);

  const refreshAuth = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  const role = user?.role || 'client';
  const isCA = role === 'ca';
  const isClient = role !== 'ca';

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    hasCompany: user?.has_company || false,
    defaultCompanyId: user?.default_company_id || null,
    role,
    isCA,
    isClient,
    loginWithGoogle,
    loginWithEmail,
    register,
    logout,
    refreshAuth,
    refreshUser: refreshAuth,
    updateUser,
    login: loginWithGoogle,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
